import { ALL_DOORS } from "../lib/state/visualizerStore";
import type { LegendRow } from "../lib/state/selectors";
import { CheckIcon, TrashIcon } from "./icons";

// Shared card/pill styling for the "All doors" entry and the per-door rows, so the All entry reads
// as a true peer of the door cards (same footprint, same selected treatment).
const rowStyle = (sel: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
  textAlign: "left",
  width: "100%",
  padding: "10px 12px",
  cursor: "pointer",
  borderRadius: "var(--radius-sm)",
  background: sel ? "var(--brand-tint)" : "var(--surface)",
  border: `1px solid ${sel ? "var(--brand)" : "var(--border)"}`,
  boxShadow: sel ? "0 0 0 3px var(--ring)" : "none",
  transition: "border-color 120ms ease, box-shadow 120ms ease, background 120ms ease",
});

const pillStyle = (sel: boolean): React.CSSProperties => ({
  flexShrink: 0,
  minWidth: 24,
  height: 24,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 7px",
  borderRadius: "var(--radius-pill)",
  fontSize: 12,
  fontWeight: 700,
  background: sel ? "var(--brand)" : "var(--bg)",
  color: sel ? "var(--on-brand)" : "var(--muted)",
});

// Per-door configuration legend: "Door 1 — Long Panel · Walnut · Bead Board".
// Clicking a row focuses that door for editing.
export function DoorLegend({ rows, selectedDoorId, showAllEntry, onSelect, onHover, onRemove }: {
  rows: LegendRow[];
  selectedDoorId: string | null;
  // Multi-door sessions get an "All doors" entry ABOVE the door cards (selected by default —
  // customer feedback: multi-door houses usually get the same style on every door). Clicking it
  // calls onSelect(ALL_DOORS); clicking a door card switches back to individual styling.
  showAllEntry?: boolean;
  onSelect: (id: string) => void;
  // Hovering a row glows the matching door on the photo so the customer sees which door it refers to.
  onHover?: (id: string | null) => void;
  // When provided, each row shows a delete affordance that asks the page to confirm removal.
  onRemove?: (id: string) => void;
}) {
  if (rows.length === 0) return null;
  const allSel = selectedDoorId === ALL_DOORS;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {showAllEntry && (
        <div
          role="button"
          tabIndex={0}
          data-testid="door-all"
          onClick={() => onSelect(ALL_DOORS)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(ALL_DOORS); } }}
          style={rowStyle(allSel)}
        >
          <span style={pillStyle(allSel)}>All</span>
          <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>All doors</span>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>One style applied to every door</span>
          </span>
        </div>
      )}
      {rows.map((r) => {
        const sel = r.id === selectedDoorId;
        return (
          // A div (not a button) so the trash control can be a real nested <button>.
          <div
            key={r.id}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(r.id)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(r.id); } }}
            onMouseEnter={() => onHover?.(r.id)}
            onMouseLeave={() => onHover?.(null)}
            style={rowStyle(sel)}
          >
            <span style={pillStyle(sel)}>
              {r.index + 1}
            </span>
            <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Door {r.index + 1}</span>
              <span style={{ fontSize: 12, color: r.configured ? "var(--muted)" : "var(--muted-2)" }}>
                {r.configured ? r.names.join(" · ") : "Not configured yet"}
              </span>
            </span>
            {/* Per-door photoreal state at a glance: shown only while this door's AI blend is fresh
                for its current configuration (editing the door or re-rendering clears it). */}
            {r.blended && (
              <span
                title="Photorealistic preview ready for this door"
                data-testid="door-photoreal"
                style={{
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "2px 8px",
                  borderRadius: "var(--radius-pill)",
                  fontSize: 11,
                  fontWeight: 600,
                  background: "var(--brand-tint)",
                  color: "var(--brand-active)",
                }}
              >
                <CheckIcon size={11} /> Photoreal
              </span>
            )}
            <span
              title={r.size === "single" ? "Single-car door (8×7)" : "Double-car door (16×7)"}
              style={{
                flexShrink: 0,
                padding: "2px 8px",
                borderRadius: "var(--radius-pill)",
                fontSize: 11,
                fontWeight: 600,
                background: "var(--brand-tint)",
                color: "var(--brand-active)",
              }}
            >
              {r.size === "single" ? "Single" : "Double"}
            </span>
            {onRemove && (
              <button
                type="button"
                title="Remove door"
                aria-label={`Remove Door ${r.index + 1}`}
                data-testid="door-remove"
                onClick={(e) => { e.stopPropagation(); onRemove(r.id); }}
                style={{
                  flexShrink: 0,
                  // 44px hit area with negative margins so the row keeps its 28px-effective layout
                  // height — a missed trash tap must not silently switch the selected door.
                  width: 44,
                  height: 44,
                  margin: "-8px -6px -8px -2px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "var(--radius-sm)",
                  border: "1px solid transparent",
                  background: "transparent",
                  color: "var(--muted-2)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--danger)"; e.currentTarget.style.borderColor = "var(--border)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--muted-2)"; e.currentTarget.style.borderColor = "transparent"; }}
              >
                <TrashIcon size={16} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
